import { Bell, BookOpen, Menu, Search } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { NAV_GROUPS } from './navItems';
import { useAuth } from '@/lib/auth/useAuth';
import { ALL_CLIENT_ROLES } from '@/types/auth';
import { hasAnyRole } from '@/lib/auth/permissions';

interface TopBarProps {
  onMenuClick: () => void;
  onCmdClick?: () => void;
}

function usePageTitle(): string {
  const { pathname } = useLocation();

  for (const group of NAV_GROUPS) {
    const match = group.items.find(
      (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
    );
    if (match) return match.label;
  }

  const extras: Record<string, string> = {
    '/live-usage':          'Live Usage',
    '/credits':             'Buy Credits',
    '/marketing/campaigns': 'Campaign Stats',
    '/settings/api-keys':   'API & Webhooks',
  };
  for (const [prefix, label] of Object.entries(extras)) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return label;
  }

  return 'Dashboard';
}

export function TopBar({ onMenuClick, onCmdClick }: TopBarProps) {
  const title = usePageTitle();
  const { user } = useAuth();
  const isClient = hasAnyRole(user?.roles ?? [], ALL_CLIENT_ROLES);

  return (
    <header className="sticky top-0 z-30 flex h-[60px] items-center gap-3 border-b border-border/80 bg-surface/95 px-4 backdrop-blur-md sm:px-6">
      {/* Mobile menu toggle */}
      <button
        type="button"
        onClick={onMenuClick}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg lg:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-4.5 w-4.5" />
      </button>

      {/* Search — opens command palette */}
      <button
        type="button"
        onClick={onCmdClick}
        className="hidden sm:flex items-center gap-2 h-8 rounded-lg border border-border bg-surface-subtle px-3 text-xs text-fg-subtle transition-colors hover:border-border-strong hover:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 w-52 lg:w-72"
        aria-label="Open command palette (Ctrl+K)"
      >
        <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="flex-1 text-left truncate">
          {isClient ? 'Search transactions…' : 'Search clients, events…'}
        </span>
        <span className="kbd hidden lg:inline-flex">/</span>
      </button>

      {/* Page title — shown on mobile only */}
      <h1 className="truncate text-[14px] font-semibold tracking-tight text-fg sm:hidden">
        {title}
      </h1>

      {/* Right controls */}
      <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
        {/* Docs shortcut */}
        {isClient && (
          <Link
            to="/portal/docs"
            className="hidden sm:flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg"
            aria-label="Developer documentation"
            title="Developer Docs"
          >
            <BookOpen className="h-4 w-4" />
          </Link>
        )}

        {/* Notification bell — visual badge */}
        <button
          type="button"
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {/* Static badge — no real notification system yet */}
          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary-500 pulse-soft" aria-hidden />
        </button>

        <div className="mx-1 h-4 w-px bg-border" aria-hidden />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
